import React from 'react';

import clsx from 'clsx';

import ActionsCell from './ActionsCell';
import ActionCell from './ActionCell';
import AvatarCell from './AvatarCell';
import BadgeCell from './BadgeCell';
import BooleanCell from './BooleanCell';
import ChipsCell from './ChipsCell';
import CountCell from './CountCell';
import CustomCell from './CustomCell';
import DateCell from './DateCell';
import DateTimeCell from './DateTimeCell';
import FileFormatCell from './FileFormatCell';
import FileNameCell from './FileNameCell';
import FileSizeCell from './FileSizeCell';
import IconCell from './IconCell';
import MessageCell from './MessageCell';
import MimeTypeCell from './MimeTypeCell';
import NumberCell from './NumberCell';
import IdCell from './IdCell';
import TextCell from './TextCell';
import UserCell from './UserCell';
import StatusCell from './StatusCell';
import { Flex, Skeleton } from '@/components/ui';
import type { BadgeColor, BadgeVariant } from '../Badge';

export type DataCellType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'boolean'
  | 'badge'
  | 'status'
  | 'avatar'
  | 'icon'
  | 'actions'
  | 'action'
  | 'message'
  | 'user'
  | 'fileSize'
  | 'fileFormat'
  | 'fileName'
  | 'mimeType'
  | 'id'
  | 'count'
  | 'chips'
  | 'custom';

export interface DataCellProps {
  type?: DataCellType;
  value?: unknown;
  className?: string;
  render?: () => React.ReactNode;
  // Type-specific props
  format?: string; // for dates, numbers
  variant?: BadgeVariant; // for badges, buttons
  size?: 'small' | 'medium' | 'large';
  color?: BadgeColor;
  // Additional props for specific cell types
  actions?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  }>;
  src?: string; // for avatar
  alt?: string; // for avatar
  icon?: React.ReactNode; // for icon cell
  user?: {
    id: string | number;
    first_name?: string;
    last_name?: string;
    name?: string;
    email?: string;
    avatar?: string;
  }; // for user cell
  showEmail?: boolean; // for user cell
  disableHoverCard?: boolean; // for user cell
  viewMode?: 'compact' | 'full'; // for user cell - override global preference
  bytes?: number; // for fileSize cell
  precision?: number; // for fileSize cell
  filename?: string; // for fileName, fileFormat cells
  extension?: string; // for fileFormat cell
  mimeType?: string; // for fileName, mimeType cells
  showIcon?: boolean; // for fileName cell
  truncate?: boolean; // for fileName, text cells
  endSlot?: React.ReactNode; // for fileName cell
  showGroup?: boolean; // for mimeType cell
  showTime?: boolean; // for date, datetime cells
  relative?: boolean; // for datetime, time cells
  serverTimezone?: string; // for datetime cells (default: America/New_York)
  // For chips type
  maxVisible?: number; // maximum number of visible chips
  chipSize?: 'xsmall' | 'small' | 'medium'; // size of chips
  chipColor?: BadgeColor; // color of chips
  chipVariant?: BadgeVariant; // variant of chips
  // For badge type
  startIcon?: React.ReactNode; // start icon for badge
  endIcon?: React.ReactNode; // end icon for badge
  highContrast?: boolean; // high contrast for badge
  // For status type
  entityType?:
    | 'import_mapping'
    | 'file'
    | 'upload'
    | 'wizard_step'
    | 'file_mapping'
    | 'import'
    | 'csv_file'; // entity type for status mapping
  status?: string; // explicit status value (if different from value)
  label?: string; // custom label override for status
  // For action type
  actionMap?: Record<
    string,
    {
      color?: BadgeColor;
      icon?: React.ReactNode;
      label?: string;
    }
  >;
  // For message type
  messageType?: 'error' | 'warning' | 'success' | 'info' | 'default'; // message type
  messageMaxWidth?: string; // max width for message
  // Render without Flex wrapper (useful for DataList and other contexts)
  noWrapper?: boolean;
}

function DataCell({
  type = 'text',
  value,
  className,
  render,
  format,
  variant,
  size,
  color,
  actions,
  src,
  alt,
  icon,
  user,
  showEmail,
  disableHoverCard,
  viewMode,
  bytes,
  precision,
  filename,
  extension,
  mimeType,
  showIcon,
  truncate,
  endSlot,
  showGroup,
  showTime,
  relative,
  serverTimezone,
  maxVisible,
  chipSize,
  chipColor,
  chipVariant,
  startIcon,
  endIcon,
  highContrast,
  actionMap,
  messageType,
  messageMaxWidth,
  entityType,
  status,
  label,
  noWrapper = false,
}: DataCellProps) {
  const cellProps = {
    value,
    format,
    variant,
    size,
    color,
    actions,
    src,
    alt,
    icon,
    user,
    showEmail,
    disableHoverCard,
    viewMode,
    bytes,
    precision,
    filename,
    extension,
    mimeType,
    showIcon,
    truncate,
    endSlot,
    showGroup,
    showTime,
    relative,
    serverTimezone,
    maxVisible,
    chipSize,
    chipColor,
    chipVariant,
    startIcon,
    endIcon,
    highContrast,
    actionMap,
    type: messageType,
    maxWidth: messageMaxWidth,
    entityType,
    status,
    label,
    className,
    render,
  };

  const renderContent = () => {
    if (render) {
      return render();
    }

    switch (type) {
      case 'text':
        return <TextCell {...cellProps} />;
      case 'number':
        return <NumberCell {...cellProps} />;
      case 'date':
        return <DateCell {...cellProps} />;
      case 'datetime':
      case 'time':
        return <DateTimeCell {...cellProps} />;
      case 'boolean':
        return <BooleanCell {...cellProps} />;
      case 'badge':
        return <BadgeCell {...cellProps} />;
      case 'status':
        return <StatusCell {...cellProps} />;
      case 'avatar':
        return <AvatarCell {...cellProps} />;
      case 'icon':
        return <IconCell {...cellProps} />;
      case 'actions':
        return <ActionsCell {...cellProps} />;
      case 'user':
        return <UserCell {...cellProps} />;
      case 'fileSize':
        return <FileSizeCell {...cellProps} />;
      case 'fileFormat':
        return (
          <FileFormatCell
            {...(cellProps as React.ComponentProps<typeof FileFormatCell>)}
          />
        );
      case 'fileName':
        return <FileNameCell {...cellProps} />;
      case 'mimeType':
        return <MimeTypeCell {...cellProps} />;
      case 'id':
        return <IdCell {...cellProps} />;
      case 'count':
        return <CountCell {...cellProps} />;
      case 'chips':
        return <ChipsCell {...cellProps} />;
      case 'action':
        return <ActionCell {...cellProps} />;
      case 'message':
        return <MessageCell {...cellProps} />;
      case 'custom':
        return <CustomCell {...cellProps} />;
      default:
        return <TextCell {...cellProps} />;
    }
  };

  const content = renderContent();

  // If noWrapper is true, return content directly without Flex wrapper
  if (noWrapper) {
    return <>{content}</>;
  }

  // Default behavior: wrap content in Flex
  return (
    <Flex
      className={clsx('data-cell', `data-cell--${type}`, className)}
      asChild
    >
      {content}
    </Flex>
  );
}

// Default skeleton component
export function DefaultDataCellSkeleton() {
  return (
    <Skeleton
      style={{
        width: '120px',
        minWidth: 16,
        height: 16,
        borderRadius: '4px',
      }}
    />
  );
}

export { DataCell };
